import { useState, FormEvent, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/common/layout/Header";
import Footer from "../../components/common/layout/Footer";
import { GradientButton } from "../../components/common/ui/Button";
import { createCustomer, findCustomerByEmail, getCustomerDetails } from "@/services/dreamscapeApi";
import {
  Mail, Lock, AlertCircle, Loader2, LogIn, Eye, EyeOff, CheckCircle2
} from "lucide-react";
import { verifyLocalPassword, setLocalPassword } from "@/utils/auth";
import { createSession } from "@/utils/session";
import { countries, findCountryDialCode } from "@/utils/countries";
import { API_URL } from "@/config/api";
import { fetchCartFromBackend } from "@/utils/cart";
import { CurrencyCode } from "@/utils/currencyConverter";
import { isValidABN, isValidACN } from "@/utils/validation";

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || null;

  const [loading, setLoading] = useState<boolean>(false);
  const [isNewUser, setIsNewUser] = useState<boolean>(false);
  const [otpMode, setOtpMode] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    organization: "",
    accountType: "personal" as 'personal' | 'business',
    businessName: "",
    businessNumberType: "ABN",
    businessNumber: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    const customerId = localStorage.getItem("customerId");
    if (customerId && redirectTo) {
      navigate(redirectTo);
    }
  }, [navigate, redirectTo]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = isNewUser ? "Email is required" : "Email, Username or ID is required";
    } else if (isNewUser && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (isNewUser) {
      if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
      if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
      if (!formData.phone.trim()) newErrors.phone = "Phone is required";
      if (!formData.address.trim()) newErrors.address = "Address is required";
      if (!formData.city.trim()) newErrors.city = "City is required";
      if (!formData.state.trim()) newErrors.state = "State/Province is required";
      if (!formData.zip.trim()) newErrors.zip = "ZIP/Postal code is required";

      if (formData.accountType === 'business') {
        if (!formData.businessName.trim()) newErrors.businessName = "Business name is required";
        if (!formData.businessNumber.trim()) {
          newErrors.businessNumber = "Business number is required";
        } else if (formData.businessNumberType === 'ABN' && !isValidABN(formData.businessNumber)) {
          newErrors.businessNumber = "Invalid ABN format or checksum";
        } else if (formData.businessNumberType === 'ACN' && !isValidACN(formData.businessNumber)) {
          newErrors.businessNumber = "Invalid ACN format or checksum";
        }
      }

      if (!formData.password.trim() || formData.password.trim().length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      }
      if (formData.password.trim() !== formData.confirmPassword.trim()) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    } else {
      if (!formData.password.trim()) {
        newErrors.password = "Password is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Handle OTP verification step during registration
    if (isNewUser && otpMode) {
      if (!otp || otp.length < 6) {
        setErrors({ otp: "Please enter a valid 6-digit OTP" });
        return;
      }

      setLoading(true);
      try {
        const verifyRes = await fetch(`${API_URL}/auth/verify-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: formData.email, otp, identifier: formData.email }),
        });
        const verifyData = await verifyRes.json();

        if (!verifyData.success) {
          setLoading(false);
          showToast(verifyData.message || "Invalid OTP code", "error");
          return;
        }

        // OTP verified! Proceed with registration
        showToast("Email verified! Finalizing registration...", "success");
      } catch (err) {
        setLoading(false);
        console.error("OTP verification error:", err);
        showToast("Failed to verify OTP. Please try again.", "error");
        return;
      }
    }

    setLoading(true);

    try {
      if (isNewUser) {
        if (!otpMode) {
          // Request OTP step
          try {
            const otpRes = await fetch(`${API_URL}/auth/request-otp`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ identifier: formData.email, context: "register" }),
            });
            const otpData = await otpRes.json();

            if (!otpData.success) {
              setLoading(false);
              showToast(otpData.message || "Failed to send verification email.", "error");
              return;
            }

            setOtpMode(true);
            setLoading(false);
            showToast("A verification code was sent to your email. Please enter it below.", "success");
            return;
          } catch (err) {
            setLoading(false);
            console.error("Failed to request OTP:", err);
            showToast("Failed to send verification email.", "error");
            return;
          }
        }

        // Proceed to actually create the customer after OTP is verified
        // 1. Create Dreamscape Customer
        const result = await createCustomer({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          organization: formData.accountType === 'business' ? formData.businessName : undefined,
          accountType: formData.accountType,
          businessName: formData.accountType === 'business' ? formData.businessName : undefined,
          businessNumberType: formData.accountType === 'business' ? formData.businessNumberType : undefined,
          businessNumber: formData.accountType === 'business' ? formData.businessNumber : undefined,
        }, { password: formData.password.trim() });

        if (result.success && result.customerId) {
          // 2. Register in our Local Backend for persistence/security
          try {
            await fetch(`${API_URL}/auth/register`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: formData.email,
                password: formData.password.trim(),
                firstName: formData.firstName,
                lastName: formData.lastName,
                customerId: result.customerId,
                username: result.username,
                accountType: formData.accountType,
                businessName: formData.accountType === 'business' ? formData.businessName : undefined,
                businessNumberType: formData.accountType === 'business' ? formData.businessNumberType : undefined,
                businessNumber: formData.accountType === 'business' ? formData.businessNumber : undefined,
              })
            });
          } catch (backendErr) {
            console.warn("Could not register in backend, continuing with local session:", backendErr);
          }

          // Auth: create the centralized logic session with provided details
          createSession(result.customerId, formData.email, result.username, formData.firstName, formData.lastName, 60);

          // Set default currency based on signup country
          const country = (formData.country || '').toUpperCase();
          let prefCurrency: CurrencyCode = 'USD';
          if (country === 'NP' || country === 'NEPAL') prefCurrency = 'NRS';
          else if (country === 'AU' || country === 'AUSTRALIA') prefCurrency = 'AUD';
          localStorage.setItem('preferredCurrency', prefCurrency);

          // Re-sync cart and navigate
          await fetchCartFromBackend(formData.email);
          navigate(redirectTo || "/checkout");
        } else {
          console.error("Registration failed:", result.message);
          showToast(result.message || "Failed to create account. Please ensure all data is correct.", "error");
        }
      } else {
        // LOGIN FLOW
        // 1. Try Backend Login
        try {
          const resp = await fetch(`${API_URL}/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              identifier: formData.email, // Send as generic identifier
              password: formData.password.trim()
            })
          });
          const backendData = await resp.json();

          if (backendData.success) {
            const user = backendData.user;
            // Link password hash locally for offline-ish checks if still used
            setLocalPassword(user.customerId, formData.password.trim());

            // Session contains everything required for auth and basic display
            createSession(user.customerId, user.email, user.username, user.firstName, user.lastName, 60);

            await fetchCartFromBackend(user.email);

            // Fetch more details to get country for currency
            try {
              const customerRes = await getCustomerDetails(user.customerId);
              if (customerRes.success && customerRes.customer) {
                const country = (customerRes.customer.country || customerRes.customer.country_code || '').toUpperCase();
                let prefCurrency: CurrencyCode = 'USD';
                if (country === 'NP' || country === 'NEPAL') prefCurrency = 'NRS';
                else if (country === 'AU' || country === 'AUSTRALIA') prefCurrency = 'AUD';
                localStorage.setItem('preferredCurrency', prefCurrency);
              }
            } catch (e) {
              console.warn("Failed to sync currency on login:", e);
            }

            navigate(redirectTo || "/checkout");
            return;
          }
        } catch (backendErr) {
          console.error("Backend auth failed, falling back to legacy/local checks:", backendErr);
        }

        // 2. Legacy / Local Fallback (check if already linked on this browser)
        const customerIdStr = localStorage.getItem("customerId");
        const storedEmail = localStorage.getItem("customerEmail");
        const storedUsername = localStorage.getItem("customerUsername") || undefined;

        if (customerIdStr && storedEmail && storedEmail.toLowerCase() === formData.email.toLowerCase()) {
          const customerId = parseInt(customerIdStr);
          const ok = verifyLocalPassword(customerId, formData.password);
          if (ok) {
            const storedFirstName = localStorage.getItem("customerFirstName") || undefined;
            const storedLastName = localStorage.getItem("customerLastName") || undefined;
            createSession(customerId, storedEmail, storedUsername, storedFirstName, storedLastName, 60);
            await fetchCartFromBackend(storedEmail);
            navigate(redirectTo || "/checkout");
            return;
          }
        }

        // 3. Final Fallback: Check Dreamscape directly
        const dsResult = await findCustomerByEmail(formData.email);
        if (dsResult.success && dsResult.customerId) {
          showToast(`Success! We found your legacy account. For security, please click "Forgot Password" below to set a new password and link your old account to our new secure dashboard. This helps us verify it's really you!`, 'success');
        } else {
          showToast("Invalid email or password. If you haven't registered, please Sign Up first.", 'error');
        }
      }
    } catch (err) {
      console.error("Auth error:", err);
      showToast(err instanceof Error ? err.message : "An unexpected error occurred. Please try again.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Shared input classes — no inline style needed
  const inputCls = `w-full px-4 py-2.5 rounded-lg text-white placeholder-white/20 text-sm
    bg-white/5 border border-white/10
    focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/40
    transition-all duration-200`;

  const labelCls = "block text-sm font-medium mb-2 text-white";

  return (
    <div className="min-h-screen text-white relative overflow-hidden">
      {/* Background — untouched */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#000000e0] via-[#1a1a3e] to-[#000000e0] opacity-50" />
      </div>

      <Header />

      <div className="container pt-8 pb-12 px-4 relative z-10">
        <div className={`${isNewUser ? "max-w-3xl" : "max-w-md"} mx-auto`}>

          {/* ── Card ── */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-white/10 shadow-2xl hover:border-indigo-500/30 transition-all">

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-brandStart to-brandEnd rounded-full mb-4 shadow-lg">
                <LogIn className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2 text-white">
                {isNewUser ? "Create Account" : "Login"}
              </h1>
              <p className="text-white text-sm">
                {isNewUser
                  ? "Create an account to continue with your purchase"
                  : "Sign in to your account to continue"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">

              {isNewUser && otpMode ? (
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-2">Verify Your Email</h3>
                  <p className="text-white/70 text-sm mb-6">
                    We've sent a 6-digit code to <strong>{formData.email}</strong>.
                  </p>
                  <label className={labelCls}>OTP Code <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className={`${inputCls} text-center tracking-widest text-2xl font-bold py-4`}
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                  {errors.otp && <p className="mt-1 text-sm text-red-400 font-bold uppercase">{errors.otp}</p>}

                  <button
                    type="button"
                    className="text-branding hover:text-brandStart text-sm mt-4 inline-block"
                    onClick={() => { setOtpMode(false); setOtp(""); }}
                  >
                    Change Email or Use Different Account
                  </button>
                </div>
              ) : (
                <>
                  {/* Email */}
                  <div>
                    <label className={labelCls}>
                      {isNewUser ? "Email Address" : "Email, Username or ID"} <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white" />
                      <input
                        type={isNewUser ? "email" : "text"}
                        value={formData.email}
                        onChange={(e) => updateFormData("email", e.target.value)}
                        className={`${inputCls} pl-10`}
                        placeholder={isNewUser ? "your@email.com" : "Email, Username or ID"}
                      />
                    </div>
                    {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
                  </div>

                  {isNewUser ? (
                    <>
                      {/* Account Type Selection Tabs */}
                      <div className="flex p-1 bg-white/5 rounded-xl border border-white/10 mb-6">
                        <button
                          type="button"
                          onClick={() => updateFormData("accountType", "personal")}
                          className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-lg transition-all ${formData.accountType === 'personal' ? 'bg-branding text-white shadow-lg' : 'text-white/40 hover:text-white'}`}
                        >
                          Private Account
                        </button>
                        <button
                          type="button"
                          onClick={() => updateFormData("accountType", "business")}
                          className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-lg transition-all ${formData.accountType === 'business' ? 'bg-branding text-white shadow-lg' : 'text-white/40 hover:text-white'}`}
                        >
                          Business Account
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                        {/* First Name */}
                        <div>
                          <label className={labelCls}>First Name <span className="text-red-400">*</span></label>
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => updateFormData("firstName", e.target.value)}
                            className={inputCls}
                            placeholder="John"
                            required
                          />
                          {errors.firstName && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.firstName}</p>}
                        </div>

                        {/* Last Name */}
                        <div>
                          <label className={labelCls}>Last Name <span className="text-red-400">*</span></label>
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => updateFormData("lastName", e.target.value)}
                            className={inputCls}
                            placeholder="Doe"
                            required
                          />
                          {errors.lastName && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.lastName}</p>}
                        </div>

                        {/* Phone */}
                        <div className="md:col-span-2">
                          <label className={labelCls}>Phone Number <span className="text-red-400">*</span></label>
                          <div className="relative">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm select-none font-bold">
                              +{findCountryDialCode(formData.country)}
                            </div>
                            <input
                              type="tel"
                              value={formData.phone}
                              onChange={(e) => updateFormData("phone", e.target.value.replace(/[^\d]/g, ""))}
                              className={`${inputCls} pl-16`}
                              placeholder="000 000 0000"
                              required
                            />
                          </div>
                          {errors.phone && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.phone}</p>}
                        </div>

                        {/* Business Fields (Conditional) */}
                        {formData.accountType === 'business' && (
                          <>
                            <div className="md:col-span-2">
                              <label className={labelCls}>Business Legal Name <span className="text-red-400">*</span></label>
                              <input
                                type="text"
                                value={formData.businessName}
                                onChange={(e) => updateFormData("businessName", e.target.value)}
                                className={inputCls}
                                placeholder="Company Pty Ltd"
                                required
                              />
                              {errors.businessName && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.businessName}</p>}
                            </div>

                            <div>
                              <label className={labelCls}>ID Type <span className="text-red-400">*</span></label>
                              <select
                                value={formData.businessNumberType}
                                onChange={(e) => updateFormData("businessNumberType", e.target.value)}
                                className={inputCls}
                              >
                                <option value="ABN" className="bg-[#1a1a2e]">ABN (Australia)</option>
                                <option value="ACN" className="bg-[#1a1a2e]">ACN (Australia)</option>
                                <option value="Company Number" className="bg-[#1a1a2e]">Company Number</option>
                                <option value="Tax ID" className="bg-[#1a1a2e]">Tax ID / EIN</option>
                                <option value="Other" className="bg-[#1a1a2e]">Other</option>
                              </select>
                            </div>

                            <div>
                              <label className={labelCls}>Business Number <span className="text-red-400">*</span></label>
                              <input
                                type="text"
                                value={formData.businessNumber}
                                onChange={(e) => updateFormData("businessNumber", e.target.value)}
                                className={inputCls}
                                placeholder="12 345 678 901"
                                required
                              />
                              {errors.businessNumber && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.businessNumber}</p>}
                            </div>
                          </>
                        )}

                        {/* Country */}
                        <div className="md:col-span-2">
                          <label className={labelCls}>Country <span className="text-red-400">*</span></label>
                          <select
                            value={formData.country}
                            onChange={(e) => updateFormData("country", e.target.value)}
                            className={inputCls}
                          >
                            {countries.map((c) => (
                              <option key={c.code} value={c.code} className="bg-[#1a1a2e] text-white">
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Address Fields */}
                        <div className="md:col-span-2">
                          <label className={labelCls}>Street Address <span className="text-red-400">*</span></label>
                          <input
                            type="text"
                            value={formData.address}
                            onChange={(e) => updateFormData("address", e.target.value)}
                            className={inputCls}
                            placeholder="Unit 12, 123 Main St"
                            required
                          />
                          {errors.address && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.address}</p>}
                        </div>

                        <div>
                          <label className={labelCls}>City <span className="text-red-400">*</span></label>
                          <input
                            type="text"
                            value={formData.city}
                            onChange={(e) => updateFormData("city", e.target.value)}
                            className={inputCls}
                            placeholder="Sydney"
                            required
                          />
                          {errors.city && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.city}</p>}
                        </div>

                        <div>
                          <label className={labelCls}>State/Province <span className="text-red-400">*</span></label>
                          <input
                            type="text"
                            value={formData.state}
                            onChange={(e) => updateFormData("state", e.target.value)}
                            className={inputCls}
                            placeholder="NSW"
                            required
                          />
                          {errors.state && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.state}</p>}
                        </div>

                        <div>
                          <label className={labelCls}>Post Code <span className="text-red-400">*</span></label>
                          <input
                            type="text"
                            value={formData.zip}
                            onChange={(e) => updateFormData("zip", e.target.value)}
                            className={inputCls}
                            placeholder="2000"
                            required
                          />
                          {errors.zip && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.zip}</p>}
                        </div>

                        {/* Password Fields */}
                        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                          <div>
                            <label className={labelCls}>Secure Password <span className="text-red-400">*</span></label>
                            <div className="relative group/pass">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 group-focus-within/pass:text-branding transition-colors" />
                              <input
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={(e) => updateFormData("password", e.target.value)}
                                className={`${inputCls} pl-10 pr-10`}
                                placeholder="••••••••"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                              >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                              </button>
                            </div>
                            {errors.password && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.password}</p>}
                          </div>

                          <div>
                            <label className={labelCls}>Confirm Password <span className="text-red-400">*</span></label>
                            <div className="relative group/pass">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 group-focus-within/pass:text-branding transition-colors" />
                              <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={formData.confirmPassword}
                                onChange={(e) => updateFormData("confirmPassword", e.target.value)}
                                className={`${inputCls} pl-10 pr-10`}
                                placeholder="••••••••"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                              >
                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                              </button>
                            </div>
                            {errors.confirmPassword && <p className="mt-1 text-[10px] text-red-400 font-bold uppercase">{errors.confirmPassword}</p>}
                          </div>
                        </div>

                      </div>
                    </>
                  ) : (
                    /* Password — login mode */
                    <div>
                      <label className={labelCls}>
                        Password <span className="text-red-400">*</span>
                      </label>
                      <div className="relative group/pass">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 group-focus-within/pass:text-branding transition-colors" />
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => updateFormData("password", e.target.value)}
                          className={`${inputCls} pl-10 pr-10`}
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.password && <p className="mt-1 text-sm text-red-400 font-bold uppercase">{errors.password}</p>}
                    </div>
                  )}
                </>
              )}

              {/* Submit */}
              <GradientButton
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-brandStart to-brandEnd hover:from-brandStart/90 hover:to-brandEnd/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold shadow-lg transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin inline" />
                    {isNewUser ? (otpMode ? "Verifying..." : "Creating Account...") : "Logging in..."}
                  </>
                ) : (
                  isNewUser ? (otpMode ? "Verify Email" : "Create Account") : "Login"
                )}
              </GradientButton>

              {/* Forgot password */}
              {!isNewUser && (
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="w-full mt-2 text-sm text-white hover:text-white transition"
                >
                  Forgot password?
                </button>
              )}

              {/* Toggle login / signup */}
              <div className="text-center pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    setIsNewUser(!isNewUser);
                    setOtpMode(false);
                    setOtp("");
                    setErrors({});
                  }}
                  className="text-branding hover:text-brandStart text-sm font-medium transition-colors"
                >
                  {isNewUser
                    ? "Already have an account? Login"
                    : "Don't have an account? Create one"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-20 right-6 z-[100] px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 text-sm font-medium transition-all animate-in slide-in-from-right-4 slide-out-to-right-4 duration-300 ${toast.type === 'success' ? 'bg-green-500/20 border border-green-500/40 text-green-300' : 'bg-red-500/20 border border-red-500/40 text-red-300'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}